import { useMemo } from 'react'
import { useNavigate } from 'react-router'

import Avatar from '@/elements/Avatar'
import Button from '@/elements/Button'
import ExternalLink from '@/elements/ExternalLink'
import Label from '@/elements/Label'
import Tag from '@/elements/Tag'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Timestamp from '@/elements/Timestamp'
import ImageGrid from '@/components/ImageGrid'
import type { AlertResponse } from '@/api/types/Alert'
import type { SubjectResponse } from '@/api/types/Subject'
import useBreakpoints from '@/hooks/useBreakpoints'

interface AlertDetailsProps {
  userData: SubjectResponse | undefined
  alertData: AlertResponse | undefined
}

function AlertDetails({ userData, alertData }: AlertDetailsProps) {
  const navigate = useNavigate()
  const { isMobile } = useBreakpoints()

  const alertAsetUrls = useMemo(() => {
    return alertData?.assets?.filter(asset => asset.url || asset.externalUrl)
      .map(asset => asset.url || asset.externalUrl || '') || []
  }, [alertData])

  return (
    <>
      <Flex wfull wrap>
        <Label>Affected Subject</Label>
        <Flex wfull wrap inline={!isMobile} justify='space-between' align='center' gap={2}>
          <Flex inline align='center' gap={2} wfull={isMobile}>
            <Avatar
              name={userData?.user?.details.fullName || ''}
              src={userData?.user?.assets?.PROFILE_PICTURE || ''}
            />
            <Flex>
              <Text variant='h4'>
                {userData?.user?.details.fullName || 'Unknown User'}
              </Text>
              <Text color='black-light-40'>{userData?.group?.name || '--'}</Text>
            </Flex>
          </Flex>
          {userData?.user.subject && (
            <Flex inline wfull justify='flex-end' align='center' gap={2} grow>
              <Button
                fullWidth={isMobile}
                variant='secondary'
                onClick={() => navigate(`/profile/${userData.user.subject}`)}
              >
                View Profile
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex wfull grow>
        <Label>Description</Label>
        <Text color='black'>{alertData?.alertDetails.description || '--'}</Text>
      </Flex>

      <Flex wfull grow>
        <Label>Source</Label>
        <ExternalLink
          to={alertData?.alertDetails.sourceHref ?? ''}
          hasIcon={true}
          maxLines={1}
          wrapped
        >
          {alertData?.alertDetails.sourceHref}
        </ExternalLink>
      </Flex>

      {alertAsetUrls.length === 0 ? null : (
        <Flex wfull grow>
          <Label>Assets</Label>
          <ImageGrid images={alertAsetUrls} />
        </Flex>
      )}

      <Flex wfull align='stretch' inline={!isMobile} gap={2} gv={4}>
        <Flex wfull style={{ width: !isMobile ? 'calc(50% - 4px)' : '100%' }}>
          <Label>Reported at</Label>
          <Flex hfull justify='center'>
            <Timestamp from={alertData?.alertDetails.createdAt ?? ''} size='large' />
          </Flex>
        </Flex>
        <Flex style={{ width: !isMobile ? 'calc(50% - 4px)' : '100%' }}>
          <Label>Reported by</Label>
          <Flex hfull justify='center'>
            <Tag variant={alertData?.alertDetails.source} size='large'>
              {alertData?.alertDetails.source || ''}
            </Tag>
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

export default AlertDetails
